import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AiIdentifyPanel from '../AiIdentifyPanel';
import { MAX_PHOTOS, mayRequestAi, uploadForIdentification } from '../../utils/aiIdentify';

/*
 * "Identify with AI" beside the photographs of a field form: the hub's field
 * collection (components/hub/FieldIntakeForm.js) and the map's observation
 * wizard (components/map/ObservationWizard.js).
 *
 * The photos go up the way the Identify page sends them (utils/aiIdentify.js),
 * each with its view where the model knows it (cap, gills, stipe, base,
 * habitat), and the AI panel answers as everywhere: safety first, a labelled
 * certainty, and a one-click "use this name" only at High certainty and never
 * when a dangerous look-alike is flagged (utils/aiThresholds.js). Using the
 * name fills the form (`onAccept`); the person still saves.
 *
 * The answer is kept with the record: `onChange` gets {aiId, modelVersion,
 * top, accepted, sawAiFirst} for the batch, and Field Mode's outbox links the
 * prediction to the record once it exists (POST /ai/identifications/{id}/link),
 * recording a name that was accepted and kept as an 'ai_image' identification
 * (utils/fieldOutbox.js).
 *
 * Shown only to the roles that may ask (routes/ai.py REQUEST_ROLES) and once
 * there is a photograph; with no connection the button waits and says why.
 *
 * After intake (2026-10-15) it serves a record already saved too: Field Mode's
 * record page passes the record's own photographs as `{photoId, view}` -- the
 * server looks their keys up, so nothing is uploaded again -- and `latest`,
 * the AI's newest answer about the record (GET
 * /collections/{id}/ai-identifications), which it shows with every candidate
 * and its percentage until the person asks again. The batch editor passes
 * `latest` for the answer kept on the batch.
 *
 * Field Mode's camera opens its post-capture summary on Save
 * (FieldCaptureSummary.js, 2026-10-16) and passes `autoAsk`: the photographs
 * go up and the AI is asked at once -- once, when the person may ask, the
 * phone is online and nothing disables it; with no connection it waits for
 * one, and a failed upload leaves the button rather than trying again by
 * itself. `pickAny` lets any listed candidate be chosen (AiIdentifyPanel's
 * rule), with "None of these" giving the choice back (`accepted: null`).
 * `compact`, `requestConfig`, `onResult` and `onError` go to the panel.
 */

/** Field Mode's photo views, as the model's view roles (routes/ai.py VIEW_ROLES). */
export const AI_VIEW_ROLES = {
  cap_top: 'cap', fertile_layer: 'gills', stipe: 'stipe', stipe_base: 'base', habitat: 'habitat',
};

const usableOf = photos => (photos || []).filter(p => p && (p.file || p.photoId));

/** What to send, and the roles: the views the model knows, else any
 *  photographs, at most MAX_PHOTOS. Files go up first (`files`); a record's
 *  own photographs go by id (`photoIds`), the server finding their keys. */
export function chooseForAi(photos) {
  const usable = usableOf(photos);
  const known = usable.filter(p => AI_VIEW_ROLES[p.view]);
  const picked = (known.length ? known : usable).slice(0, MAX_PHOTOS);
  const roles = known.length ? picked.map(p => AI_VIEW_ROLES[p.view]) : null;
  if (picked.length && picked.every(p => p.photoId)) {
    return { files: [], photoIds: picked.map(p => p.photoId), roles };
  }
  const files = picked.filter(p => p.file);
  return {
    files: files.map(p => p.file), photoIds: [], roles: known.length ? files.map(p => AI_VIEW_ROLES[p.view]) : null,
  };
}

const signatureOf = photos => usableOf(photos)
  .map(p => (p.photoId ? `${p.view}:id:${p.photoId}`
    : `${p.view}:${p.file.name || ''}:${p.file.size || 0}:${p.file.lastModified || 0}`)).join('|');

/** The batch's / record's note of an answer: what the outbox and the record
 *  page need to link it later. `mine` false for someone else's answer, which
 *  only they may link. */
export const answerOf = (result, sawAiFirst, mine = true) => {
  const top = (result?.predictions || [])[0];
  return {
    aiId: result.ai_id,
    modelVersion: result.model_version || null,
    top: top ? { taxon_name: top.taxon_name, confidence: top.confidence } : null,
    accepted: null,
    sawAiFirst,
    mine,
  };
};

// A Blob kept on the device has no name; the upload names the key after it.
const asFile = (blob, i) => (blob && blob.name ? blob
  : new File([blob], `field-${i + 1}.jpg`, { type: (blob && blob.type) || 'image/jpeg' }));

function useOnline(override) {
  const [online, setOnline] = useState(() => typeof navigator === 'undefined' || navigator.onLine !== false);
  useEffect(() => {
    if (override !== undefined) return undefined;
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => { window.removeEventListener('online', up); window.removeEventListener('offline', down); };
  }, [override]);
  return override !== undefined ? override : online;
}

export default function AiFieldSuggestion({
  photos, user, value, onChange, onAccept, online: onlineProp, disabled = false, hadOwnName = false,
  upload = uploadForIdentification, testid = 'field-ai', latest = null, keptKey = 'field.kept',
  autoAsk = false, pickAny = false, compact = false, requestConfig, onResult, onError,
}) {
  const { t } = useTranslation('ai');
  const online = useOnline(onlineProp);
  const [phase, setPhase] = useState('idle'); // idle | uploading | asked
  const [asked, setAsked] = useState(null);   // { keys, photoIds, roles, signature, sawAiFirst }
  const [error, setError] = useState('');
  const answer = useRef(null);
  const signature = signatureOf(photos);
  const allowed = mayRequestAi(user);
  // Held back while the photos went up (the camera learnt that identification
  // runs only in production, the session ended, ...): nothing is asked, and
  // the keys are kept for when it is allowed again, so they go up once.
  const disabledRef = useRef(disabled);
  disabledRef.current = disabled;
  const uploaded = useRef(null);   // { signature, keys }
  const autoAsked = useRef(false);

  const ask = async () => {
    setError('');
    setPhase('uploading');
    const { files, photoIds, roles } = chooseForAi(photos);
    try {
      const named = files.map(asFile);
      const kept = uploaded.current?.signature === signature ? uploaded.current.keys : null;
      const keys = photoIds.length ? []
        : kept || await (requestConfig ? upload(named, requestConfig) : upload(named));
      if (disabledRef.current) {
        uploaded.current = { signature, keys };
        autoAsked.current = false;
        setPhase(asked ? 'asked' : 'idle');
        return;
      }
      uploaded.current = null;
      answer.current = null;
      // Blind only when the person named it first AND no answer of the AI's
      // has been shown here: a name taken from the AI, or an answer already
      // on screen, is not a blind judgement (routes/review_queue.py counts
      // saw_ai_first false as the live accuracy).
      setAsked({ keys, photoIds, roles, signature, sawAiFirst: !hadOwnName || !!value?.aiId || !!latest });
      setPhase('asked');
    } catch (e) {
      setPhase(asked ? 'asked' : 'idle');
      setError(t('field.uploadFailed'));
    }
  };

  // Asked by itself, once (the camera's summary): only when a person who may
  // ask has photographs, a connection and nothing holding it back; offline it
  // waits for the connection. A failed upload is not tried again by itself;
  // one held back as the photos went up is asked once the hold lifts.
  const askRef = useRef(ask);
  askRef.current = ask;
  useEffect(() => {
    if (!autoAsk || autoAsked.current || !allowed || !signature || !online || disabled
      || phase !== 'idle' || asked) return;
    autoAsked.current = true;
    askRef.current();
  }, [autoAsk, allowed, signature, online, disabled, phase, asked]);

  if (!allowed || (!signature && !latest)) return null;
  const stale = !!asked && asked.signature !== signature;
  // An answer given earlier and shown again has been seen, unless this
  // batch knows it was asked blind; the server does not send the flag back.
  const seenLatest = !(latest && value?.aiId === latest.ai_id && value?.sawAiFirst === false);

  const heard = (result) => {
    onResult?.(result);
    if (!result?.ai_id) return;
    answer.current = answerOf(result, asked?.sawAiFirst ?? !hadOwnName);
    onChange?.(answer.current);
  };

  // "None of these": the answer stays kept, without a chosen name.
  const none = () => {
    const meta = { ...(answer.current || value || {}), accepted: null };
    answer.current = meta;
    onChange?.(meta);
  };

  const accept = (chosen) => {
    // Accepted from the answer given earlier: noted as that answer's, and
    // whose it is (only its asker may link it).
    const earlier = latest && value?.aiId !== latest.ai_id
      ? answerOf(latest, seenLatest, latest.mine !== false) : null;
    const meta = { ...(answer.current || earlier || value || {}),
      accepted: { taxon_name: chosen.taxon_name, rank: chosen.rank } };
    answer.current = meta;
    onChange?.(meta);
    onAccept?.(chosen);
  };

  const showButton = (phase !== 'asked' || stale) && !!signature;
  const askLabel = stale || latest ? t('field.askAgain') : t('field.button');
  return (
    <div data-testid={testid} style={box}>
      {showButton && (
        <button type="button" onClick={ask} disabled={!online || disabled || phase === 'uploading'}
          title={t('field.tip')} data-testid={`${testid}-run`} style={button(!online || disabled)}>
          <span aria-hidden="true">✨ </span>
          {phase === 'uploading' ? t('field.uploading') : askLabel}
        </button>
      )}
      {!online && <p role="note" style={note} data-testid={`${testid}-offline`}>{t('field.offline')}</p>}
      {stale && <p role="note" style={note} data-testid={`${testid}-stale`}>{t('field.photosChanged')}</p>}
      {error && <p role="alert" style={{ ...note, color: 'var(--c-danger-fg)' }}>{error}</p>}
      {phase === 'asked' && asked && (
        <AiIdentifyPanel key={[...asked.keys, ...asked.photoIds].join('|')} s3Key={asked.keys[0]}
          additionalS3Keys={asked.keys.slice(1)} photoIds={asked.photoIds.length ? asked.photoIds : undefined}
          viewRoles={asked.roles || undefined} sawAiFirst={asked.sawAiFirst} autoRun
          onResult={heard} onAccept={accept}
          {...(compact ? { compact: true } : {})}
          {...(requestConfig ? { requestConfig } : {})}
          {...(onError ? { onError } : {})}
          {...(pickAny ? { pickAny: true, picked: value?.accepted?.taxon_name ?? null, onPickNone: none } : {})} />
      )}
      {phase !== 'asked' && latest && (
        <div data-testid={`${testid}-latest`}>
          <p style={note}>
            {latest.mine === false && latest.requested_by_name
              ? t('field.latestBy', { when: shortMoment(latest.created_at), who: latest.requested_by_name })
              : t('field.latest', { when: shortMoment(latest.created_at) })}
            {latest.accepted ? ` ${t('field.latestAccepted', { taxon: latest.accepted.taxon_name })}` : ''}
          </p>
          <AiIdentifyPanel key={latest.ai_id} initialResult={latest} sawAiFirst={seenLatest}
            onAccept={latest.accepted ? undefined : accept} />
        </div>
      )}
      {value?.aiId && (value.aiId !== latest?.ai_id || value.accepted) && (
        <p style={note} data-testid={`${testid}-kept`}>
          {value.accepted ? t(`${keptKey}Accepted`, { taxon: value.accepted.taxon_name }) : t(keptKey)}
        </p>
      )}
    </div>
  );
}

/** When an answer was given, in the device's own words. */
const shortMoment = (value) => {
  const ms = Date.parse(String(value || '').replace(' ', 'T'));
  return Number.isFinite(ms) ? new Date(ms).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' }) : '';
};

const box = { display: 'flex', flexDirection: 'column', gap: 6 };
const note = { margin: 0, fontSize: 13, color: 'var(--c-text-muted-fg)' };
const button = off => ({
  alignSelf: 'flex-start', minHeight: 44, padding: '8px 14px', borderRadius: 6, fontSize: 14, fontWeight: 600,
  border: '1px solid var(--c-primary)', background: 'var(--c-surface)', color: 'var(--c-primary-fg)',
  cursor: off ? 'not-allowed' : 'pointer', opacity: off ? 0.6 : 1,
});
